import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { AppHeader } from '../components/AppHeader.js';
import { TestSelectPage } from '../components/TestSelectPage.js';
import { Toast } from '../components/Toast.js';
import { LearnPhase } from '../components/LearnPhase.js';
import {
  fetchDictionary,
  fetchSession,
  postAnswer,
  postAnswersBatch,
} from '../lib/api.js';
import {
  UNKNOWN_TARGET,
  appendUnknowns,
  decideNextPhase,
  planPagePosts,
} from '../lib/test-page-logic.js';
import type { DictEntryWithExamples } from '../lib/types.js';

function placeholderEntry(word: string): DictEntryWithExamples {
  return {
    word,
    phonetic: null,
    translation_zh: '(暂无释义)',
    coca_rank: null,
    pos: null,
    exchange_raw: null,
    example_1_en: null,
    example_1_zh: null,
    example_2_en: null,
    example_2_zh: null,
  };
}

export function Test(): JSX.Element {
  const params = useParams<{ contentId: string }>();
  const contentId = decodeURIComponent(params.contentId);
  const [, setLocation] = useLocation();

  const [currentEntries, setCurrentEntries] = useState<DictEntryWithExamples[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [unknownAccumulated, setUnknownAccumulated] = useState<string[]>([]);
  const [phase, setPhase] = useState<'test' | 'learn'>('test');
  const [learnWords, setLearnWords] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [shownCount, setShownCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const goToDone = useCallback(
    (stats: { known: number; unknown: number }) => {
      const qs = new URLSearchParams({
        known: String(stats.known),
        unknown: String(stats.unknown),
        unsure: '0',
      });
      setLocation(`/done/${encodeURIComponent(contentId)}?${qs.toString()}`);
    },
    [setLocation, contentId],
  );

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    setLoadingInitial(true);
    fetchSession(contentId)
      .then(async (sess) => {
        if (cancelled) return;
        if (sess.pending_words.length === 0) {
          if (sess.learn_queue.length > 0) {
            setLearnWords(sess.learn_queue);
            setPhase('learn');
            setLoadingInitial(false);
            return;
          }
          setLoadingInitial(false);
          goToDone({ known: 0, unknown: 0 });
          return;
        }
        if (sess.round_picks.length === 0) {
          setLoadingInitial(false);
          return;
        }
        const entries = await Promise.all(
          sess.round_picks.map((w) =>
            fetchDictionary(w).catch<DictEntryWithExamples>(() => placeholderEntry(w)),
          ),
        );
        if (cancelled) return;
        setCurrentEntries(entries);
        setLoadingInitial(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadingInitial(false);
        setError((e as Error).message ?? '加载失败');
      });
    return () => {
      cancelled = true;
    };
  }, [contentId, goToDone]);

  const handleToggle = useCallback((word: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(word)) next.delete(word);
      else next.add(word);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting || currentEntries.length === 0) return;
    setSubmitting(true);

    const plan = planPagePosts({
      pageEntries: currentEntries.map((e) => ({ word: e.word })),
      selectedOnPage: selected,
    });

    // One batched POST: the server processes all 10 answers atomically and
    // returns the next session state in the same response, so we don't need
    // a follow-up fetchSession. Replaces what used to be 10 sequential POSTs
    // + 1 GET (~11 round-trips) with 1 round-trip.
    let resp;
    try {
      resp = await postAnswersBatch(contentId, {
        mode: 'test',
        answers: plan.map((p) => ({ word: p.word, quality: p.quality })),
      });
    } catch {
      setSubmitting(false);
      setToast('保存失败，重试');
      return;
    }

    const newlyUnknownInOrder = currentEntries
      .map((e) => e.word)
      .filter((w) => selected.has(w));
    const nextAcc = appendUnknowns(unknownAccumulated, newlyUnknownInOrder);
    const newShown = shownCount + currentEntries.length;
    setUnknownAccumulated(nextAcc);
    setShownCount(newShown);

    const sess = resp.next_session;
    const decision = decideNextPhase({
      unknownAccumulated: nextAcc,
      remainingPool: sess.pending_words.length,
      nextRoundPicksLength: sess.round_picks.length,
    });

    if (decision.kind === 'done') {
      setSubmitting(false);
      goToDone({ known: newShown, unknown: 0 });
      return;
    }
    if (decision.kind === 'learn') {
      setLearnWords(decision.words);
      setPhase('learn');
      setSubmitting(false);
      return;
    }

    const entries = await Promise.all(
      sess.round_picks.map((w) =>
        fetchDictionary(w).catch<DictEntryWithExamples>(() => placeholderEntry(w)),
      ),
    );
    setCurrentEntries(entries);
    setSelected(new Set());
    setPageIndex((i) => i + 1);
    setSubmitting(false);
  }, [
    submitting,
    currentEntries,
    selected,
    unknownAccumulated,
    shownCount,
    contentId,
    goToDone,
  ]);

  if (phase === 'learn') {
    return (
      <>
        <AppHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-4">
          <LearnPhase
            initialWords={learnWords}
            onAnswer={async (word, correct) => {
              const ans = await postAnswer(contentId, {
                word,
                quality: correct ? 5 : 0,
                mode: 'learn',
              });
              return { familiarity: ans.familiarity, familiarity_before: ans.familiarity_before };
            }}
            onComplete={() =>
              goToDone({
                known: Math.max(0, shownCount - unknownAccumulated.length),
                unknown: unknownAccumulated.length,
              })
            }
          />
        </main>
        {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}
      </>
    );
  }

  const liveUnknown = unknownAccumulated.length + selected.size;
  const progressPct = Math.min(100, (liveUnknown / UNKNOWN_TARGET) * 100);

  return (
    <>
      <AppHeader />
      <div className="px-4 -mt-1 mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-secondary text-hint">
            已挑出 {liveUnknown} / {UNKNOWN_TARGET}
          </span>
          <span className="text-muted text-hint">第 {pageIndex + 1} 页</span>
        </div>
        <div className="h-1 w-full bg-surface-2 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
      {error ? (
        <div className="text-secondary text-center max-w-card mx-auto mt-6">
          加载失败
          <div className="text-muted text-hint mt-2 break-words">{error}</div>
        </div>
      ) : loadingInitial ? (
        <div className="text-secondary text-hint text-center mt-6">载入中...</div>
      ) : (
        <TestSelectPage
          entries={currentEntries}
          selected={selected}
          submitting={submitting}
          onToggle={handleToggle}
          onSubmit={handleSubmit}
        />
      )}
      {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}
    </>
  );
}
